/**
 * Alert engine for staff panels.
 *
 * Loops a chime + vibration whenever there's at least one un-acknowledged
 * order on the panel, and stops automatically when:
 *   • the chef/bartender/shisha advances any item (advanceOrder/updateStatus)
 *   • the order leaves the Received column
 *   • staff visit the panel (visibility change) and the audio gets gestured
 *
 * Manager role uses single-chime mode: plays once when a new order/service
 * bell arrives, then quiet until the next new one.
 *
 * Each panel calls `useOrderAlert({ role, orders })`. The hook tracks the set
 * of received-order IDs and fires accordingly.
 */
import { useEffect, useRef, useCallback, useState } from 'react';

// Built-in sounds packaged as tiny base64 WAVs would be huge — instead we use
// the Web Audio API to synthesise a clean lounge-friendly chime that doesn't
// need a download and works on every device.
const SOUND_PROFILES = {
  chime:     [{ f: 880, d: 0.15 }, { f: 1320, d: 0.20 }],                                       // pleasant bell
  bell:      [{ f: 1046, d: 0.20 }, { f: 1318, d: 0.20 }, { f: 1568, d: 0.30 }],                 // softer hotel bell
  ring:      [{ f: 1200, d: 0.10 }, { f: 0,    d: 0.05 }, { f: 1200, d: 0.10 }, { f: 0, d: 0.05 }, { f: 1200, d: 0.10 }], // urgent triple-ring
  klaxon:    [{ f: 440, d: 0.20 }, { f: 660, d: 0.20 }, { f: 440, d: 0.20 }, { f: 660, d: 0.20 }], // alarm
};

function playSound(audioCtx, profile = 'chime', volume = 0.7) {
  const notes = SOUND_PROFILES[profile] || SOUND_PROFILES.chime;
  let t = audioCtx.currentTime;
  for (const note of notes) {
    if (note.f === 0) { t += note.d; continue; }
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = note.f;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(volume, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t + note.d);
    osc.connect(gain).connect(audioCtx.destination);
    osc.start(t);
    osc.stop(t + note.d + 0.05);
    t += note.d;
  }
}

// Per-device persisted preference. Stores ONLY UI prefs (mute / sound /
// volume) — no PII or auth tokens, so localStorage is appropriate.
const STORAGE_KEY = 'majnu:alertPrefs';
const DEFAULT_PREFS = { muted: false, sound: 'chime', volume: 0.7 };

export function loadAlertPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : DEFAULT_PREFS;
  } catch { return DEFAULT_PREFS; }
}
export function saveAlertPrefs(prefs) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...DEFAULT_PREFS, ...prefs })); }
  catch { /* private mode */ }
}

/**
 * @param {Object} opts
 *  - role: 'chef' | 'bartender' | 'shisha_expert' | 'manager'
 *  - orders: array of order docs (the panel's current data)
 *  - getReceivedItems: (order) => Array — defaults to items where status='received'
 */
export function useOrderAlert({ role, orders, getReceivedItems }) {
  const [prefs, setPrefs] = useState(loadAlertPrefs);
  const updatePrefs = useCallback((next) => {
    const merged = { ...prefs, ...next };
    setPrefs(merged);
    saveAlertPrefs(merged);
  }, [prefs]);

  const audioCtxRef = useRef(null);
  const seenOrderIdsRef = useRef(new Set()); // orders we've already chimed for at least once
  const loopTimerRef = useRef(null);
  const looping = role === 'chef' || role === 'bartender' || role === 'shisha_expert';

  // Bootstrap audio context on the first user gesture. iOS Safari refuses to
  // play sound otherwise. Listening once on the document.
  useEffect(() => {
    const unlock = () => {
      if (audioCtxRef.current) return;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      } catch (e) { console.warn('AudioContext unlock failed:', e); }
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  const doPlay = useCallback(() => {
    if (prefs.muted) return;
    if (!audioCtxRef.current) return; // needs user gesture first
    try { playSound(audioCtxRef.current, prefs.sound, prefs.volume); } catch (e) { /* */ }
    try { if (navigator.vibrate) navigator.vibrate(looping ? [300, 100, 300, 100, 300] : [200, 80, 200]); } catch { /* */ }
  }, [prefs, looping]);

  // Stop the looping chime
  const stopLoop = useCallback(() => {
    if (loopTimerRef.current) {
      clearInterval(loopTimerRef.current);
      loopTimerRef.current = null;
    }
  }, []);

  // Decide what to do every time `orders` changes
  useEffect(() => {
    // Active received-item order IDs (the ones that demand attention)
    const pickReceived = getReceivedItems || ((o) => (o.items || []).filter(i => i.status === 'received'));
    const pendingIds = new Set(
      (orders || [])
        .filter(o => pickReceived(o).length > 0)
        .map(o => o.id)
    );

    // Detect brand-new unseen orders
    const newOnes = [...pendingIds].filter(id => !seenOrderIdsRef.current.has(id));
    if (newOnes.length > 0) {
      doPlay();
      newOnes.forEach(id => seenOrderIdsRef.current.add(id));
    }

    // Looping panels: keep ringing while there's at least one pending order
    if (looping) {
      if (pendingIds.size > 0 && !loopTimerRef.current) {
        loopTimerRef.current = setInterval(doPlay, 5000);
      } else if (pendingIds.size === 0 && loopTimerRef.current) {
        stopLoop();
      }
    }

    // Clean up seen IDs that are no longer in the orders list
    seenOrderIdsRef.current = new Set([...seenOrderIdsRef.current].filter(id => pendingIds.has(id)));
  }, [orders, doPlay, looping, stopLoop, getReceivedItems]);

  // Clean up on unmount
  useEffect(() => () => stopLoop(), [stopLoop]);

  // Listen for push events from the service worker — fires the same sound
  // even if the polling hasn't picked up the new order yet.
  useEffect(() => {
    if (!navigator.serviceWorker) return;
    const onMsg = (event) => {
      const d = event.data || {};
      if (d.type !== 'majnu-push') return;
      if (d.category === 'new_order' || d.category === 'stale_order' || d.category === 'service_bell') {
        doPlay();
      }
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [doPlay]);

  return { prefs, updatePrefs, stopLoop, playSample: doPlay };
}
